import * as React from "react";
import { useState } from "react";

import { Check, Pencil, X } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/utils";

// AIDEV-NOTE: InlineEditableText — click-to-edit text with pencil icon.
// Used for client names in the sidebar nav, clients table, and other places
// where inline renaming is needed. Handles its own edit state; parent provides
// the current value, onSave callback, and optional saving/error state.

export interface InlineEditableTextProps {
    value: string;
    onSave: (newValue: string) => Promise<void>;
    /** Whether a save is in progress */
    isSaving?: boolean;
    /** Text size/style for display mode */
    className?: string;
    /** Input className override */
    inputClassName?: string;
    /** Always show pencil icon (vs only on hover) */
    alwaysShowPencil?: boolean;
    /** Color for the pencil icon */
    pencilClassName?: string;
    /** Placeholder when value is empty */
    placeholder?: string;
    /**
     * Commit the typed value when focus leaves the editor, instead of requiring Enter or the
     * check button. Opt-in so existing call sites that save over the network on every commit
     * keep their explicit-confirm behavior.
     */
    commitOnBlur?: boolean;
}

export function InlineEditableText({
    value,
    onSave,
    isSaving = false,
    className,
    inputClassName,
    alwaysShowPencil = false,
    pencilClassName,
    placeholder = "Unnamed",
    commitOnBlur = false,
}: InlineEditableTextProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);

    const handleStartEdit = () => {
        setEditValue(value);
        setIsEditing(true);
    };

    const handleSave = async () => {
        if (!editValue.trim()) return;
        await onSave(editValue.trim());
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(value);
        setIsEditing(false);
    };

    // AIDEV-NOTE: EVO-2215 — the editor commits ONLY on Enter or the check button, so typing a
    // value and then clicking anything else (the panel's own Save included) silently discards it.
    // The check path works; nobody finds it. In the PCC card that left 0 of every part profile in
    // production with a typed age, against 16/17 gender and 17/17 classification — sibling
    // <Select>s that commit on change with no confirm step. relatedTarget keeps focus moves WITHIN
    // the editor (check/cancel buttons) from double-firing; an emptied field cancels rather than
    // saving, since handleSave refuses blank input and would otherwise trap the user in edit mode.
    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        if (!commitOnBlur || isSaving) return;
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
            return;
        }
        if (!editValue.trim()) {
            handleCancel();
            return;
        }
        if (editValue.trim() === value.trim()) {
            setIsEditing(false);
            return;
        }
        void handleSave();
    };

    if (isEditing) {
        return (
            <div className="flex items-center gap-1" onBlur={handleBlur}>
                <Input
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") handleSave();
                        if (e.key === "Escape") handleCancel();
                    }}
                    className={cn("h-7 text-sm", inputClassName)}
                    disabled={isSaving}
                    autoFocus
                />
                <Button
                    variant="ghostIcon"
                    size="icon"
                    onClick={handleSave}
                    disabled={isSaving || !editValue.trim()}
                    className="size-6 shrink-0"
                >
                    <Check className="size-3.5" />
                </Button>
                <Button
                    variant="ghostIcon"
                    size="icon"
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="size-6 shrink-0"
                >
                    <X className="size-3.5" />
                </Button>
            </div>
        );
    }

    return (
        <button
            onClick={handleStartEdit}
            className={cn(
                "group inline-flex items-center gap-2 transition-colors",
                className,
            )}
            title="Click to edit"
        >
            <span className="truncate">{value || placeholder}</span>
            <Pencil
                className={cn(
                    "size-3.5 shrink-0",
                    alwaysShowPencil
                        ? ""
                        : "opacity-0 group-hover:opacity-80 transition-opacity",
                    pencilClassName ?? "text-muted-foreground",
                )}
            />
        </button>
    );
}
